import React, { useMemo } from 'react';
import { Form } from '@ant-design/compatible';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { CustomAttribute } from '../../../../../../types/product/customAttribute';
import BaseForm from '../../../../../../components/form/BaseForm';
import DynamicInput from './DynamicInput';
import { formRequiredRules } from '../../../../../../lib/formRules';

type ValidationRuleConfig = { message: string; whitespace: boolean; type?: 'array' | 'number' | 'boolean' };

interface CustomAttributeInputProps {
  form: WrappedFormUtils;
  attribute: CustomAttribute;
  isEditable?: boolean;
}

const CustomAttributeInput: React.FC<CustomAttributeInputProps> = ({ form, attribute, isEditable = true }) => {
  const { key, name, nullable, value, dataType } = attribute;
  const { getFieldDecorator } = form;

  const validationConfig = useMemo<Record<string, ValidationRuleConfig>>(() => {
    const defaultMessage = `${name}은(는) 필수 입력 항목입니다.`;

    return {
      string: { message: defaultMessage, whitespace: true },
      array: { message: defaultMessage, whitespace: false, type: 'array' },
      number: { message: defaultMessage, whitespace: false, type: 'number' },
      boolean: { message: defaultMessage, whitespace: false, type: 'boolean' },
    };
  }, [name]);

  const config = useMemo<ValidationRuleConfig>(() => {
    const defaultMessage = `${name}은(는) 필수 입력 항목입니다.`;
    return (
      validationConfig[dataType.type] || {
        message: defaultMessage,
        whitespace: false,
      }
    );
  }, [validationConfig, dataType, name]);

  const { message: configMessage, whitespace: configWhitespace, type: configType } = config;

  const rules = useMemo(
    () => (nullable ? [] : [formRequiredRules(true, configMessage, configWhitespace, configType)]),
    [nullable, configMessage, configWhitespace, configType],
  );

  return (
    <BaseForm
      label={name}
      required={!nullable}
      content={
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator(key, {
            initialValue: value,
            rules,
            validateTrigger: ['onChange', 'onBlur'],
          })(<DynamicInput attribute={attribute} isEditable={isEditable} />)}
        </Form.Item>
      }
    />
  );
};

export default CustomAttributeInput;
